import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
  Grid,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { locationService } from '../services/locationService';
import { formatPhoneOnInput, isValidPhone } from '../utils/phoneFormatter';

const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    state: '',
    city: '',
    whatsapp: '',
    birthday_day: '',
    birthday_month: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [filteredStates, setFilteredStates] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  // Estilo comum para campos do formulário
  const fieldStyle = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
      },
      '&.Mui-focused': {
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
      },
    },
  };

  // Estilo para FormControl (Select)
  const selectStyle = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
      },
      '&.Mui-focused': {
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
      },
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)',
      '&.Mui-focused': {
        color: '#8B5CF6',
      },
    },
  };

  // Carregar estados ao montar o componente
  useEffect(() => {
    const loadStates = async () => {
      setLoadingStates(true);
      try {
        const statesData = await locationService.getStates();
        setStates(statesData);
        setFilteredStates(statesData);
      } catch (error) {
        setError('Erro ao carregar lista de estados');
      } finally {
        setLoadingStates(false);
      }
    };
    loadStates();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Aplicar formatação automática para telefone e WhatsApp
    let formattedValue = value;
    if (name === 'phone' || name === 'whatsapp') {
      formattedValue = formatPhoneOnInput(value);
    }
    
    setFormData({
      ...formData,
      [name]: formattedValue,
    });
  };

  // Manipular seleção de estado
  const handleStateChange = async (event, newValue) => {
    if (newValue) {
      setFormData({
        ...formData,
        state: newValue.sigla,
        city: '' // Limpar cidade quando mudar estado
      });
      
      // Carregar cidades do estado selecionado
      setLoadingCities(true);
      try {
        const citiesData = await locationService.getCitiesByState(newValue.id);
        setCities(citiesData);
        setFilteredCities(citiesData);
      } catch (error) {
        setError('Erro ao carregar lista de cidades');
      } finally {
        setLoadingCities(false);
      }
    } else {
      setFormData({
        ...formData,
        state: '',
        city: ''
      });
      setCities([]);
      setFilteredCities([]);
    }
  };

  // Filtrar estados durante a digitação
  const handleStateInputChange = (event, newInputValue) => {
    const filtered = locationService.filterStates(states, newInputValue);
    setFilteredStates(filtered);
  };

  // Manipular seleção de cidade
  const handleCityChange = (event, newValue) => {
    setFormData({
      ...formData,
      city: newValue ? newValue.nome : ''
    });
  };

  // Filtrar cidades durante a digitação
  const handleCityInputChange = (event, newInputValue) => {
    const filtered = locationService.filterCities(cities, newInputValue);
    setFilteredCities(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate required fields individually
    const requiredFields = [
      { field: 'name', label: 'Nome do DJ' },
      { field: 'email', label: 'Email' },
      { field: 'password', label: 'Senha' },
      { field: 'confirmPassword', label: 'Confirmar Senha' },
      { field: 'state', label: 'Estado' },
      { field: 'city', label: 'Cidade' },
      { field: 'birthday_day', label: 'Dia do Nascimento' },
      { field: 'birthday_month', label: 'Mês do Nascimento' }
    ];

    // Check each required field
    for (const { field, label } of requiredFields) {
      if (!formData[field] || formData[field] === '') {
        setError(`Campo obrigatório: ${label}`);
        setLoading(false);
        return;
      }
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Email inválido. Digite um email válido.');
      setLoading(false);
      return;
    }

    // Validate password length
    if (formData.password.length < 8) {
      setError('A senha deve ter pelo menos 8 caracteres');
      setLoading(false);
      return;
    }

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('As senhas não coincidem');
      setLoading(false);
      return;
    }

    // Validate phone format if provided
    if (formData.phone && formData.phone.trim() !== '') {
      if (!isValidPhone(formData.phone)) {
        setError('Telefone inválido. Use o formato: (11) 99999-9999');
        setLoading(false);
        return;
      }
    }

    // Validate WhatsApp format if provided
    if (formData.whatsapp && formData.whatsapp.trim() !== '') {
      if (!isValidPhone(formData.whatsapp)) {
        setError('WhatsApp inválido. Use o formato: (11) 99999-9999');
        setLoading(false);
        return;
      }
    }

    // Social links removidos do cadastro: Facebook/Instagram serão opcionais no dashboard

    // Convert to numbers for validation
    const birthdayDay = parseInt(formData.birthday_day);
    const birthdayMonth = parseInt(formData.birthday_month);

    // Validate birthday day
    if (isNaN(birthdayDay) || birthdayDay < 1 || birthdayDay > 31) {
      setError('Dia de nascimento deve estar entre 1 e 31');
      setLoading(false);
      return;
    }

    // Validate birthday month
    if (isNaN(birthdayMonth) || birthdayMonth < 1 || birthdayMonth > 12) {
      setError('Mês de nascimento deve estar entre 1 e 12');
      setLoading(false);
      return;
    }

    // Remove confirmPassword from data and convert birthday fields to numbers
    const { confirmPassword, ...userData } = formData;
    
    // Convert birthday fields to numbers (already validated above)
    userData.birthday_day = birthdayDay;
    userData.birthday_month = birthdayMonth;

    const result = await register(userData);
    
    if (result.success) {
      setSuccess(true);
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Card
          sx={{
            background: 'rgba(10, 10, 10, 0.8)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(0, 212, 255, 0.2)',
          }}
        >
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography
              variant="h4"
              sx={{
                fontFamily: 'Orbitron',
                fontWeight: 900,
                color: '#00D4FF',
                mb: 3,
              }}
            >
              Cadastro Realizado!
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, opacity: 0.8 }}>
              Seu cadastro foi enviado para aprovação. Você receberá um email quando sua conta for aprovada.
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: '#FF6B35', fontWeight: 600 }}>
              Atenção: verifique também a sua caixa de spam. Alguns servidores podem filtrar nossos emails.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/login')}
              sx={{
                bgcolor: '#00D4FF',
                color: '#000',
                '&:hover': {
                  bgcolor: '#33DDFF',
                },
              }}
            >
              Ir para Login
            </Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Card
        sx={{
          background: 'rgba(10, 10, 10, 0.8)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(157, 0, 255, 0.2)',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h4"
              sx={{
                fontFamily: 'Orbitron',
                fontWeight: 900,
                background: 'linear-gradient(45deg, #8B5CF6, #06B6D4)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
              }}
            >
              Quero Tocar
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.7 }}>
              Cadastre-se para ser um DJ/Locutor da NostalBeat
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Typography variant="body2" sx={{ mb: 3, color: '#9D00FF', fontWeight: 'bold' }}>
              * Campos obrigatórios
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nome do DJ *"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  helperText="Ex: DJ João Silva"
                  placeholder="Digite seu nome artístico"
                  sx={fieldStyle}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email *"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  helperText="Ex: dj@exemplo.com"
                  placeholder="seu@email.com"
                  sx={fieldStyle}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Senha *"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  helperText="Mínimo 8 caracteres"
                  placeholder="Digite sua senha"
                  sx={fieldStyle}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Confirmar Senha *"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  helperText="Digite a mesma senha"
                  placeholder="Confirme sua senha"
                  sx={fieldStyle}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Telefone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  helperText="Ex: (11) 99999-9999"
                  placeholder="(11) 99999-9999"
                  sx={fieldStyle}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={filteredStates}
                  getOptionLabel={(option) => option ? `${option.nome} - ${option.sigla}` : ''}
                  value={states.find(state => state.sigla === formData.state) || null}
                  onChange={handleStateChange}
                  onInputChange={handleStateInputChange}
                  loading={loadingStates}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Estado *"
                      placeholder="Digite o nome do estado"
                      helperText="Ex: São Paulo - SP"
                      required
                      sx={fieldStyle}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      {option.nome} - {option.sigla}
                    </Box>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={filteredCities}
                  getOptionLabel={(option) => option ? option.nome : ''}
                  value={cities.find(city => city.nome === formData.city) || null}
                  onChange={handleCityChange}
                  onInputChange={handleCityInputChange}
                  loading={loadingCities}
                  disabled={!formData.state}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cidade *"
                      placeholder="Digite o nome da cidade"
                      helperText={!formData.state ? "Selecione um estado primeiro" : "Ex: Rio de Janeiro"}
                      required
                      sx={fieldStyle}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      {option.nome}
                    </Box>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="WhatsApp"
                  name="whatsapp"
                  value={formData.whatsapp}
                  onChange={handleChange}
                  helperText="Ex: (11) 99999-9999"
                  placeholder="(11) 99999-9999"
                  sx={fieldStyle}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required sx={selectStyle}>
                  <InputLabel>Dia do Nascimento *</InputLabel>
                  <Select
                    name="birthday_day"
                    value={formData.birthday_day}
                    onChange={handleChange}
                    label="Dia do Nascimento *"
                  >
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                      <MenuItem key={day} value={day}>
                        {day.toString().padStart(2, '0')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required sx={selectStyle}>
                  <InputLabel>Mês do Nascimento *</InputLabel>
                  <Select
                    name="birthday_month"
                    value={formData.birthday_month}
                    onChange={handleChange}
                    label="Mês do Nascimento *"
                  >
                    <MenuItem value={1}>Janeiro</MenuItem>
                    <MenuItem value={2}>Fevereiro</MenuItem>
                    <MenuItem value={3}>Março</MenuItem>
                    <MenuItem value={4}>Abril</MenuItem>
                    <MenuItem value={5}>Maio</MenuItem>
                    <MenuItem value={6}>Junho</MenuItem>
                    <MenuItem value={7}>Julho</MenuItem>
                    <MenuItem value={8}>Agosto</MenuItem>
                    <MenuItem value={9}>Setembro</MenuItem>
                    <MenuItem value={10}>Outubro</MenuItem>
                    <MenuItem value={11}>Novembro</MenuItem>
                    <MenuItem value={12}>Dezembro</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                py: 1.5,
                mt: 4,
                mb: 3,
                '&:disabled': {
                  bgcolor: 'rgba(157, 0, 255, 0.3)',
                },
              }}
            >
              {loading ? <CircularProgress size={24} /> : 'Cadastrar'}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ opacity: 0.7, mb: 1 }}>
                Já tem uma conta?
              </Typography>
              <Link
                component="button"
                type="button"
                onClick={() => navigate('/login')}
                sx={{
                  color: '#8B5CF6',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Faça login aqui
              </Link>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default Register;
